import type { ReactNode } from 'react';

import { Portal } from '@/components/ui/portal';

/**
 * Draws a spotlight above the whole window on Android and web, through the
 * app's root overlay: a body-level portal on web, and on Android the overlay
 * host at the root, above the navigator and every header in it.
 *
 * A portal on native renders its children at the host, outside the screen
 * that drew them, so anything the card reads from a screen-level context has
 * to be provided again inside the card itself. The host passes touches
 * through wherever none of its own views sit.
 *
 * Mount it only while it shows: the overlay waits for an exit animation the
 * spotlight never runs, so closing it by `isOpen` alone would leave it up.
 * Android's back button calls `onRequestClose`, and does nothing without one.
 */
export function SpotlightLayer({
  onRequestClose,
  children,
}: {
  onRequestClose?: () => void;
  children: ReactNode;
}) {
  return (
    <Portal
      isOpen
      animationPreset="none"
      isKeyboardDismissable={Boolean(onRequestClose)}
      onRequestClose={onRequestClose}>
      {children}
    </Portal>
  );
}
